//@ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Country } from '../../graphql/entities';
import { AlertType, ICompanyFragment, ICountry } from '../../graphql/schema/p4types';
import LoqateFindAddressQuery from '../../graphql/queries/common/LoqateFindAddressQuery.graphql';
import LoqateRetrieveAddressQuery from '../../graphql/queries/common/LoqateRetrieveAddressQuery.graphql';
import LoqateFindAddressWithContainerQuery from '../../graphql/queries/common/LoqateFindAddressWithContainerQuery.graphql';
import { useValueState } from '../../utils/hooks';
import { Button, InfoButton } from '../../components/connectedForm/Button';
import { ComboBox } from '@fluentui/react';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import { useCompany } from '../../utils/FetchConfig';
import { Glyph } from '../../components/view/ColumnSelector';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import FluentModal from '../../components/modals/FluentModal';
import {
  FilterOperation,
  ICountriesDropdownQueryResult,
  ICountriesDropdownQueryVariables,
} from '../../graphql/schema/p4types';
import { useP4ToastController } from '../system/NotificationsArea';

export interface ILoqateAddress {
  company?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  label?: string;
  city?: string;
  countryIso3?: string;
  county?: string;
  postCode?: string;
  country?: ICountry;
}

/**
 * Searches Address and prefills form with selected address details
 * @returns {ReactElement}
 */
export const LoqateModal = ({
  onSubmit,
  hasSeparateCompanyField = false,
  disabled = false,
}: {
  onSubmit?: (address) => void;
  hasSeparateCompanyField?: boolean;
  disabled?: boolean;
}) => {
  // @ts-ignore
  const companyFragment: ICompanyFragment = useCompany();
  const onError = useComponentQueryErrorHandling();

  // Queries
  const { dispatchSimpleToast } = useP4ToastController();
  const [countryQuery] = useLazyQuery<ICountriesDropdownQueryResult, ICountriesDropdownQueryVariables>(
    Country.dropdown.query
  );
  const [loqateFindAddressQuery, { data: foundArea }] = useLazyQuery(LoqateFindAddressQuery, { onError });
  const [loqateRetrieveAddressQuery, { data: retrievedAddress }] = useLazyQuery(LoqateRetrieveAddressQuery, {
    onError,
  });
  const [loqateFindAddressWithContainerQuery, { data: foundAddresses }] = useLazyQuery(
    LoqateFindAddressWithContainerQuery,
    { onError }
  );

  // States
  const [searchText, setSearchText] = useValueState('');
  const [validCountry, setValidCountry] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [comboHidden, setComboHidden] = useState(true);
  const [fieldsHidden, setFieldsHidden] = useState(true);
  const [shouldFill, setShouldFill] = useState(false);
  const [addressOptions, setAddressOptions] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<{ key: string | number }>({ key: '' });
  const [formattedAddress, setFormattedAddress] = useState<ILoqateAddress>({});

  // Reset Function
  const resetLoqateModalState = (shouldResetSearchText) => {
    setSearchText(shouldResetSearchText ? '' : searchText);
    setShouldFill(false);
    setComboHidden(true);
    setValidCountry('');
    setFieldsHidden(true);
    setAddressOptions([]);
    setSelectedAddress({ key: '' });
    setFormattedAddress({});
  };

  useEffect(() => {
    if (foundArea?.loqateFindAddress.length > 0) {
      const addressOnlyEntries = foundArea?.loqateFindAddress.filter(
        (address) => address?.type?.toLowerCase === 'address'
      );

      // Prefer only the address types if available
      if (addressOnlyEntries?.length > 0) {
        setComboHidden(false);
        addressOnlyEntries.forEach((address) => {
          const { id, type, text, description } = address;
          setAddressOptions((prev) => [...prev, { key: id, text: `${text}, ${description}` }]);
        });
      } else {
        // Use the partial matching results to find more detail on each address
        for (const addresses of foundArea?.loqateFindAddress) {
          const { id, type, text, description } = addresses;
          setComboHidden(false);
          if (type.toLowerCase() !== 'street') {
            loqateFindAddressWithContainerQuery({ variables: { text: searchText, container: id } });
          }
        }
      }
    }
  }, [foundArea]);

  useEffect(() => {
    if (foundAddresses?.loqateFindAddressWithContainer.length > 0) {
      setComboHidden(false);

      foundAddresses?.loqateFindAddressWithContainer.map((address) => {
        const { id, text } = address;
        setAddressOptions((prev) => [...prev, { key: id, text }]);
      });
    }
  }, [foundAddresses]);

  useEffect(() => {
    if (selectedAddress.key && selectedAddress.key !== '') {
      loqateRetrieveAddressQuery({ variables: { id: selectedAddress.key } });
      setFieldsHidden(false);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (retrievedAddress?.loqateRetrieveAddress.length > 0) {
      let line1 = retrievedAddress?.loqateRetrieveAddress[0].line1;
      const { line2, line3, line4, label, company, city, countryIso3, provinceName, postalCode, countryName } =
        retrievedAddress?.loqateRetrieveAddress[0];

      // If the target doesn't support a company name, fold into line 1
      if (company && !hasSeparateCompanyField) {
        line1 = `${company}, ${line1}`;
      }

      setFormattedAddress({
        line1,
        line2,
        line3,
        line4,
        label,
        company,
        city,
        countryIso3,
        county: provinceName,
        postCode: postalCode,
        country: countryName,
      });
    }
  }, [retrievedAddress]);

  useEffect(() => {
    if (shouldFill) {
      countryQuery({
        variables: {
          filters: [
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'isoCountry.codeAlpha3',
                value: formattedAddress?.countryIso3,
              },
            },
          ],
        },
      }).then((data) => {
        const countryInSystem = data?.data?.list?.edges?.[0]?.node;

        if (countryInSystem) {
          const address = {
            company: formattedAddress?.company,
            line1: formattedAddress?.line1,
            line2: formattedAddress?.line2,
            line3: formattedAddress?.line3,
            line4: formattedAddress?.city,
            line5: formattedAddress?.county,
            postCode: formattedAddress?.postCode,
            country: countryInSystem,
          };

          if (onSubmit) {
            onSubmit(address);
          }

          setModalOpen(false);
          resetLoqateModalState(false);
        } else {
          const invalidCountryText = `${formattedAddress?.country} does not exist in the system, See the list of countries in the country dropdown`;
          setValidCountry(invalidCountryText);
          dispatchSimpleToast('Country not found', invalidCountryText, AlertType.Info);
        }
      });
    }
  }, [shouldFill]);

  if (!companyFragment?.loqateApiKeySet) {
    return null;
  }

  return disabled ? null : (
    <>
      <Glyph
        display={true}
        onClick={() => setModalOpen(true)}
        colour={'#686868'}
        icon={'envelope'}
        style={{ marginTop: '0px', marginLeft: '-46px', marginBottom: '10px' }}
      />
      <FluentModal className="modalContent" isOpen={modalOpen}>
        <Form.Fieldset cols={3} style={{ width: '600px' }}>
          <h2>Search Address</h2>
          <hr />
          <input.BlurTextField
            readOnly={false}
            active={true}
            enableOnChange={true}
            placeholder={'Post code or address line'}
            alignLabel="right"
            onChange={setSearchText}
            value={searchText}
            label={`Search`}
            labelCols={1 / 3}
            inputCols={2 / 3}
          />
          <Button
            type="button"
            label="Search"
            styles={{ root: { alignItems: 'center' } }}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
            onClick={(e) => {
              e.preventDefault();
              resetLoqateModalState(false);
              loqateFindAddressQuery({ variables: { text: searchText } });
            }}
          >
            Search
          </Button>
          <div hidden={comboHidden}>
            <hr />
            <ComboBox
              options={addressOptions}
              styles={{ container: { marginBottom: '10px' } }}
              onChange={(event, options) => {
                if (options?.key) {
                  setSelectedAddress({ key: options.key });
                }
              }}
              label="Select Address"
            />
          </div>
          <div hidden={fieldsHidden}>
            <hr />
            {hasSeparateCompanyField && formattedAddress.company ? (
              <input.BlurTextField
                label="Company"
                labelCols={1 / 3}
                inputCols={2 / 3}
                value={formattedAddress?.company}
                readOnly={true}
              />
            ) : null}
            <input.BlurTextField
              label="Address line 1"
              labelCols={1 / 3}
              inputCols={2 / 3}
              value={formattedAddress?.line1}
              readOnly={true}
            />
            <input.BlurTextField
              label="Address line 2"
              labelCols={1 / 3}
              inputCols={2 / 3}
              value={formattedAddress?.line2}
              readOnly={true}
            />
            <input.BlurTextField
              label="Address line 3"
              labelCols={1 / 3}
              inputCols={2 / 3}
              value={formattedAddress?.line3}
              readOnly={true}
            />
            <input.BlurTextField label="City" labelCols={1 / 3} inputCols={2 / 3} value={formattedAddress?.city} />
            <input.BlurTextField label="County" labelCols={1 / 3} inputCols={2 / 3} value={formattedAddress?.county} />
            <input.BlurTextField
              label="Post Code"
              labelCols={1 / 3}
              inputCols={2 / 3}
              value={formattedAddress?.postCode}
              readOnly={true}
            />
            <input.BlurTextField
              label="Country"
              validationHint={validCountry}
              labelCols={1 / 3}
              inputCols={2 / 3}
              value={formattedAddress?.country}
              readOnly={true}
            />
          </div>
          <hr />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <InfoButton
              label="Cancel"
              onClick={(e) => {
                e.preventDefault();
                setModalOpen(false);
                resetLoqateModalState(true);
              }}
            >
              Cancel
            </InfoButton>
            <Button
              primary={true}
              disabled={fieldsHidden}
              onClick={(e) => {
                e.preventDefault();
                setShouldFill(true);
              }}
            >
              Fill
            </Button>
          </div>
          <br />
        </Form.Fieldset>
      </FluentModal>
    </>
  );
};
